import { useState } from "react";
import { Eye, FileCheck2, FileImage, FileText } from "lucide-react";

import { Button } from "@/design-system/primitives";
import type { BriefDocument, PatientBriefResponse } from "@/types/clinician";

function isPdfDocument(document: BriefDocument) {
  return (
    document.mime.toLowerCase().includes("pdf") ||
    document.filename.toLowerCase().endsWith(".pdf")
  );
}

export function IdentityDocumentPreview({
  data,
}: {
  data: PatientBriefResponse;
}) {
  const [open, setOpen] = useState(false);
  const document = [...data.documents]
    .filter((entry) => entry.purpose === "id")
    .sort(
      (left, right) =>
        Date.parse(right.uploaded_at) - Date.parse(left.uploaded_at),
    )[0];
  const image = document?.mime.startsWith("image/") && document.download_url;
  const pdf = document && isPdfDocument(document);

  return (
    <>
      {document ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-5 w-[15rem] min-h-[9rem] border border-line-subtle bg-sunken p-5 text-left hover:border-ink focus-visible:outline-none focus-visible:shadow-focus"
          aria-label={`Preview photo ID: ${document.filename}`}
        >
          {image ? (
            <img
              src={document.download_url ?? undefined}
              alt="Patient photo ID"
              className="w-full h-[7rem] object-contain"
            />
          ) : pdf && document.download_url ? (
            <span className="relative h-[7rem] block overflow-hidden bg-page">
              <iframe
                src={`${document.download_url}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                title={`${document.filename} first page`}
                tabIndex={-1}
                aria-hidden="true"
                loading="lazy"
                className="absolute inset-0 w-full h-[16rem] pointer-events-none"
              />
              <span className="absolute left-4 top-4 bg-page/95 border border-line px-4 py-2 type-label-xs text-ink flex items-center gap-3 shadow-sm">
                <FileText size={12} aria-hidden /> PDF ID
              </span>
              <span className="absolute inset-x-0 bottom-0 bg-ink/85 px-5 py-3 type-label-xs text-page flex items-center justify-center gap-3">
                <Eye size={12} aria-hidden /> Preview PDF
              </span>
            </span>
          ) : (
            <span className="h-[7rem] flex flex-col items-center justify-center gap-4 text-ink-muted">
              <FileCheck2 size={22} aria-hidden />
              <span className="type-label-xs">Photo ID document</span>
              <span className="type-label-xs text-ink flex items-center gap-3">
                <Eye size={12} aria-hidden /> Preview file
              </span>
            </span>
          )}
        </button>
      ) : (
        <div className="mt-5 w-[15rem] h-[9rem] border border-dashed border-line flex flex-col items-center justify-center gap-4 text-ink-muted">
          <FileImage size={22} aria-hidden />
          <span className="type-label-xs">NO PHOTO ID</span>
        </div>
      )}

      {open && document && (
        <div
          className="fixed inset-0 z-[140] bg-ink/70 p-12 md:p-30 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label="Patient photo ID preview"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative w-full max-w-[72rem] max-h-[calc(100vh-4rem)] overflow-auto bg-page border border-line p-12"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-10 flex items-center justify-between gap-8">
              <div>
                <p className="type-label-sm">Patient photo ID</p>
                <p className="text-11 text-ink-muted">{document.filename}</p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setOpen(false)}
              >
                Close
              </Button>
            </div>
            <DocumentPreview document={document} />
          </div>
        </div>
      )}
    </>
  );
}

export function DocumentPreview({ document }: { document: BriefDocument }) {
  if (!document.download_url) {
    return (
      <p className="p-15 type-body-sm text-warning">
        A preview link is not currently available for this document.
      </p>
    );
  }

  const isImage = document.mime.startsWith("image/");
  const isPdf = isPdfDocument(document);

  return (
    <div className="p-12 flex flex-col gap-10">
      <div className="flex items-center justify-between gap-8">
        <div>
          <p className="type-body-sm font-medium">{document.filename}</p>
          <p className="text-11 text-ink-muted">
            {document.purpose.toUpperCase()} ·{" "}
            {new Date(document.uploaded_at).toLocaleString()}
          </p>
        </div>
        <Button asChild size="sm" variant="secondary">
          <a
            href={document.download_url}
            download={document.filename}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download
          </a>
        </Button>
      </div>
      {isImage ? (
        <img
          src={document.download_url}
          alt={document.filename}
          className="w-full h-auto border border-line-subtle"
        />
      ) : isPdf ? (
        <iframe
          src={document.download_url}
          title={document.filename}
          className="w-full h-[calc(100vh-10rem)] border border-line-subtle"
        />
      ) : (
        <p className="type-body-sm text-ink-muted">
          This file type cannot be previewed inline. Download it to review.
        </p>
      )}
    </div>
  );
}
